import React, { FC } from 'react';

import {
  Box,
  Text,
  Flex,
  Modal,
  Stack,
  Switch,
  Button,
  ModalBody,
  IconButton,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';
import { HiMinusCircle } from 'react-icons/hi';

import { Form, Formik } from 'formik';

import WashHubSelect from '../WashHubSelect';
import FormikCheckBox from 'components/primitives/FormikCheckBox';
import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import { SkuManufacturerDetails } from 'components/modules/SkuManufacturerDetails';

import { useSKU } from 'hooks/useSKU';
import { useManufacturers } from 'hooks/useManufacturers';

import { itemTypes } from 'constants/itemtypes';
import { EQUIPMENT_CATEGORIES } from 'types/sku';

import { getInitialValues, validationSchema } from './EditSkuModal.utils';

import { EditSkuModalViewProps } from './EditSkuModal.props';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const EditSkuModalView: FC<EditSkuModalViewProps> = (props) => {
  const {
    sku,
    type,
    isOpen,
    onClose,
    onUpdate,
    onUpdateCaseSku,
    isLoading,
    isLoadingUpdateSku,
    onUpdatePackageSku,
    isUpdatingPackageSku,
    onUpdateEquipmentSku,
    isUpdatingEquipmentSku,
  } = props;

  const { data: skus = [], isLoading: isLoadingSKUs } = useSKU('item');
  const { data: caseSkus = [], isLoading: isLoadingCaseSKUs } = useSKU('case');
  const {
    data: manufacturers = [],
    isLoading: isLoadingManufacturers,
  } = useManufacturers();
  return (
    <Modal
      size="4xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader
          paddingTop={10}
          paddingBottom={0}
          textAlign="center"
          fontWeight="600">
          {type === 'case'
            ? 'Update case SKU'
            : type === 'package'
            ? 'update package SKU'
            : type === 'equipment'
            ? 'Update equipment SKU'
            : 'Update SKU'}{' '}
          - {sku?._id || ''}
        </ModalHeader>
        <ModalBody paddingTop={0}>
          <Flex direction="column" p={6}>
            <Formik
              initialValues={getInitialValues(type, sku)}
              validationSchema={validationSchema(type)}
              onSubmit={
                type === 'case'
                  ? onUpdateCaseSku
                  : type === 'package'
                  ? onUpdatePackageSku
                  : type === 'equipment'
                  ? onUpdateEquipmentSku
                  : onUpdate
              }>
              {({ isValid, values, setFieldValue }) => (
                <Form>
                  {type === 'item' ? (
                    <>
                      <Flex marginTop={0}>
                        <FormikInput
                          name="sku"
                          label="SKU Prefix"
                          labelProps={labelProps}
                          placeholder="Your text here"
                          isRequired
                        />
                        <Box h="20px" w="50px" />
                        <FormikInput
                          name="name"
                          label="SKU Name"
                          labelProps={labelProps}
                          placeholder="Your text here"
                          isRequired
                        />
                        <Box h="20px" w="50px" />
                        <FormikInput
                          type="number"
                          name="itemsInCase"
                          label="Count Of Items in Case"
                          labelProps={labelProps}
                          placeholder="Your text here"
                          isRequired
                        />
                      </Flex>
                      <Flex mt={6}>
                        <FormikSelectInput
                          placeholder="Select Item Type"
                          name="itemType"
                          label="Item Type SKU">
                          {itemTypes?.map((i) => (
                            <option key={i} value={i}>
                              {i || ''}
                            </option>
                          ))}
                        </FormikSelectInput>
                        <Box h="20px" w="50px" />
                        <FormikInput
                          name="description"
                          label="Item Description"
                          labelProps={labelProps}
                          placeholder="Your text here"
                        />
                        <Box h="20px" w="50px" />
                        <FormikInput
                          type="text"
                          name="color"
                          label="Primary Color"
                          labelProps={labelProps}
                          placeholder="Your text here"
                        />
                      </Flex>
                      <Flex mt={6}>
                        <FormikInput
                          type="number"
                          name="weight"
                          label="Weight (Grams)"
                          labelProps={labelProps}
                          placeholder="Your text here"
                        />
                        <Box h="20px" w="50px" />
                        <FormikInput
                          name="material"
                          label="Material"
                          labelProps={labelProps}
                          placeholder="Your text here"
                        />
                        <Box h="20px" w="50px" />
                        <FormikSelectInput
                          name="manufacturer"
                          label="Manufacturer"
                          labelProps={labelProps}
                          isLoading={isLoadingManufacturers}
                          placeholder="Select manufacturer">
                          {manufacturers?.map((m) => (
                            <option key={m._id} value={m._id}>
                              {m.name}
                            </option>
                          ))}
                        </FormikSelectInput>
                      </Flex>
                      <Flex mt={6}>
                        <FormikInput
                          type="number"
                          name="size"
                          label="Size (oz)"
                          labelProps={labelProps}
                          placeholder="Your text here"
                        />
                        <Box h="20px" w="50px" />
                        <FormikInput
                          type="number"
                          name="pricePerDelivery"
                          label="Standard Price Per Delivery"
                          labelProps={labelProps}
                          placeholder="Your text here"
                          isRequired
                          roundUpOnBlur
                        />
                        <Box h="20px" w="50px" />
                        <FormikInput
                          type="number"
                          name="pricePerReplacement"
                          label="Standard Price Per Replacement"
                          labelProps={labelProps}
                          placeholder="Your text here"
                          isRequired
                          roundUpOnBlur
                        />
                      </Flex>
                      <Flex mt={6}>
                        <FormikCheckBox
                          field="qrEnabled"
                          name="Unique QR Codes for each item"
                        />
                      </Flex>
                      <SkuManufacturerDetails />
                    </>
                  ) : type === 'package' ? (
                    <>
                      <Flex>
                        <FormikInput
                          name="name"
                          label="Name"
                          type="text"
                          placeholder="Your text here"
                          labelProps={labelProps}
                          isRequired
                        />
                        <Box h="20px" w="30px" />
                        <FormikSelectInput
                          isRequired
                          name="caseSku"
                          placeholder="Select"
                          label="Case SKU"
                          isLoading={isLoadingCaseSKUs}
                          labelProps={labelProps}>
                          {caseSkus?.map((s) => (
                            <option key={s._id} value={s._id}>
                              {s?.name || ''}
                            </option>
                          ))}
                        </FormikSelectInput>
                        <Box h="20px" w="30px" />
                        <FormikInput
                          type="text"
                          name="description"
                          label="Description"
                          labelProps={labelProps}
                          placeholder="Your text here"
                        />
                      </Flex>
                      <Flex>
                        <WashHubSelect packageId={sku?._id || ''} />
                      </Flex>
                      <Flex direction="column">
                        {values?.items?.map((d: any, i: number) => {
                          const typeValue = skus?.find((s) => s._id === d.sku)
                            ?.itemType;

                          return (
                            <Flex
                              key={`${d.sku}-${i}`}
                              mt={6}
                              position="relative"
                              alignItems="flex-end"
                              flex={1}>
                              <FormikSelectInput
                                isRequired
                                id={`sku-select-${i}`}
                                placeholder="Select"
                                label="Item SKU Name"
                                isLoading={isLoadingSKUs}
                                containerProps={{ flex: 1 }}
                                name={`items[${i}].sku`}
                                labelProps={labelProps}>
                                {skus?.map((s) => (
                                  <option key={s._id} value={s._id}>
                                    {s?.name || ''}
                                  </option>
                                ))}
                              </FormikSelectInput>
                              <Box h="20px" w="10px" />
                              <FormikInput
                                isReadOnly
                                id={`type-${i}`}
                                name={`item-type`}
                                label="Item Type"
                                labelProps={labelProps}
                                containerProps={{ flex: 1 }}
                                value={typeValue}
                              />
                              <Box h="20px" w="10px" />
                              <FormikInput
                                type="number"
                                id={`count-${i}`}
                                name={`items[${i}].quantity`}
                                label="Count of items in Package"
                                labelProps={labelProps}
                                containerProps={{ flex: 1 }}
                                isRequired
                              />
                              {i !== 0 && (
                                <IconButton
                                  aria-label="remove-item"
                                  icon={
                                    <HiMinusCircle size="25px" fill="#E54444" />
                                  }
                                  size="sm"
                                  variant="ghost"
                                  position="absolute"
                                  right={-8}
                                  mb={1}
                                  onClick={() =>
                                    setFieldValue(
                                      'items',
                                      values?.items?.filter(
                                        (_: any, j: number) => j !== i,
                                      ),
                                    )
                                  }
                                />
                              )}
                            </Flex>
                          );
                        })}
                      </Flex>
                      <Flex flex={1} mt={8} w="100%">
                        {values && values?.items && values?.items?.length <= 5 && (
                          <Button
                            w="100%"
                            size="lg"
                            borderRadius="5px"
                            variant="ghost"
                            bg="primary.900"
                            onClick={() =>
                              setFieldValue('items', [
                                ...values.items,
                                {
                                  sku: '',
                                  quantity: '',
                                },
                              ])
                            }>
                            Add Item (max 6)
                          </Button>
                        )}
                      </Flex>
                    </>
                  ) : type === 'equipment' ? (
                    <>
                      <Flex marginTop={0}>
                        <FormikSelectInput
                          isRequired
                          name="category"
                          label="Category"
                          labelProps={labelProps}
                          placeholder="Select category">
                          {EQUIPMENT_CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </FormikSelectInput>
                        <Box h="20px" w="50px" />
                        <FormikInput
                          type="text"
                          name="name"
                          label="SKU Name"
                          labelProps={labelProps}
                          placeholder="Your text here"
                          isRequired
                        />
                        <Box h="20px" w="50px" />
                        <FormikSelectInput
                          name="manufacturer"
                          label="Manufacturer"
                          labelProps={labelProps}
                          isLoading={isLoadingManufacturers}
                          placeholder="Select manufacturer">
                          {manufacturers?.map((m) => (
                            <option key={m._id} value={m._id}>
                              {m.name}
                            </option>
                          ))}
                        </FormikSelectInput>
                      </Flex>
                    </>
                  ) : (
                    <>
                      <Flex>
                        <FormikInput
                          type="text"
                          name="prefix"
                          label="SKU Prefix"
                          labelProps={labelProps}
                          placeholder="Your text here"
                          isRequired
                        />
                        <Box h="20px" w="50px" />
                        <FormikInput
                          type="text"
                          name="name"
                          label="SKU Name"
                          labelProps={labelProps}
                          placeholder="Your text here"
                          isRequired
                        />
                        <Box h="20px" w="50px" />
                        <FormikInput
                          type="text"
                          name="description"
                          label="Description"
                          labelProps={labelProps}
                          placeholder="Your text here"
                        />
                      </Flex>
                    </>
                  )}
                  <Flex direction="column">
                    <Text {...labelProps} mt="20px" fontWeight="bold">
                      Status
                    </Text>
                    <Flex mt="10px" align="center" gap="20px">
                      <Text>Inactive</Text>
                      <Switch
                        isChecked={values?.isSuspended}
                        onChange={(e) => {
                          setFieldValue('isSuspended', e.target.checked);
                        }}
                        colorScheme="primary"
                      />
                    </Flex>
                  </Flex>

                  <Stack
                    my={12}
                    align="center"
                    flexDirection="row"
                    justifyContent="center">
                    <Button
                      isDisabled={
                        isLoading ||
                        isLoadingUpdateSku ||
                        isUpdatingPackageSku ||
                        isUpdatingEquipmentSku
                      }
                      w="200px"
                      onClick={onClose}
                      variant="outline">
                      Cancel
                    </Button>
                    <Button
                      style={{ marginTop: '0', marginLeft: 10 }}
                      type="submit"
                      isDisabled={!isValid}
                      isLoading={
                        isLoading ||
                        isLoadingUpdateSku ||
                        isUpdatingPackageSku ||
                        isUpdatingEquipmentSku
                      }
                      w="200px">
                      Update
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditSkuModalView;
